/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useDispatch} from 'react-redux';

/**
 * The `useGridRefreshAction` hook provides a convenience utility that accepts
 * a `refresh` boolean and one or more action creators which are to be dispatched
 * when the `refresh` argument is `true`.
 *
 * ```typescript
 *   import {useGridRefreshAction} from '@ion/applications/src/hooks/useGridRefreshAction';
 *
 *   const [refreshRequired, setRefreshRequired] = useState(false);
 *   ...
 *
 *   // dispatch a single action creator which refresh is true ...
 *   useGridRefreshAction(refreshRequired, adminAssetsGridRefreshed);
 *
 *   ...
 *
 *   // dispatch multiple actions creators when refresh is true ...
 *   useGridRefreshAction(
 *     refreshRequired,
 *     adminUsersGridRefreshed,
 *     adminUsersLoadRoles
 *   );
 * ```
 *
 * @param refresh When true, the provided action creator will be dispatched.
 * @param action One or more Redux Action Creators which are to be dispatched
 *        when the refresh argument is true.
 */
export const useGridRefreshAction = (refresh: boolean, ...actions) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (refresh) {
      actions.forEach(action => {
        dispatch(action());
      });
    }
  }, [refresh]);
};
export default useGridRefreshAction;
